import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useClient } from 'jsonapi-react';
import toast from 'react-hot-toast';
import moment from 'moment';
import { FiInfo } from 'react-icons/fi';
import { DashboardContent } from '@/layouts/dashboard';
import { TiaBanner } from '@/components/custom/tia-banner';
import { Loader } from '@/components/custom/loader';
import { Alert, AlertTitle } from '@/components/ui/alert';
import tiaRedacaoConvite from '@/assets/images/tia/TIA-REDACAO-CONVITE.png';
import tiaSurpresa from '@/assets/images/tia/TIA-SURPRESA.png';
import calendarIcon from '@/assets/images/icons/calendar.png';
import notesIcon from '@/assets/images/icons/notes.svg';
import pencilIcon from '@/assets/images/icons/pencil.svg';
import selfieIcon from '@/assets/images/icons/selfie.svg';
import { PATHS } from '@/routes/paths';

const ROMAN_NUMERALS = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X'];

const HOW_TO_SEND_STEPS = essayId => [
  {
    icon: pencilIcon,
    title: 'Escreva sua redação',
    description: (
      <>
        Baixe nossa folha e imprima, ou use uma folha pautada tamanho A4. <br />
        <Link
          to={PATHS.ESSAY.PRINT(essayId)}
          className="font-extrabold underline"
        >
          Baixar folha
        </Link>
      </>
    )
  },
  {
    icon: selfieIcon,
    title: 'Tire foto da redação',
    description: (
      <>
        Antes de fotografar, siga estas dicas:
        <ul className="mt-1 list-disc space-y-0.5 pl-4">
          <li>Enquadre a folha inteira, sem cortar as linhas.</li>
          <li>Escolha um lugar plano e bem iluminado.</li>
          <li>Escreva com letra legível, sem rasuras.</li>
          <li>Deixe os espaços entre as palavras sempre do mesmo tamanho.</li>
        </ul>
      </>
    )
  },
  {
    icon: null,
    emoji: '😉',
    title: 'Revise o texto digitalizado',
    description: 'Confira se o texto ficou igual ao que você escreveu. Para corrigir algo, toque no texto e edite. Quando estiver tudo certo, toque em "Enviar para correção".'
  }
];

function AboutCard({ title, children }) {
  return (
    <div className="rounded-2xl border border-neutral-lightest bg-white p-5 shadow-card">
      <h3 className="mb-3 text-base font-extrabold text-neutral-900">{title}</h3>
      {children}
    </div>
  );
}

export default function EssayAboutPlayful({ essayId, uri }) {
  const client = useClient();
  const [essay, setEssay] = useState(null);
  const [loading, setLoading] = useState(true);

  const getEssay = async () => {
    setLoading(true);
    const { data, error } = await client.fetch(`/essays/${essayId}`);
    if (error) {
      if (error.title === 'Essay not available') {
        toast.error('Redação não disponível');
        window.location.href = '/redacao';
      } else {
        toast.error('Erro ao buscar redação');
      }
    } else {
      setEssay(data);
    }
    setLoading(false);
  };

  useEffect(() => {
    getEssay();
  }, []);

  const breadcrumbs = {
    title: 'Redação',
    nav: [
      {
        route: '/redacao',
        name: 'Redação',
        isActive: false
      },
      {
        route: uri,
        name: essay?.title ? essay?.title : essay?.topic,
        isActive: true
      }
    ]
  };

  const canSend = essay?.status === 'accepting_proposal' || essay?.status === 'minimum_length_not_satisfied' || (essay?.status === 'ocr_review' && essay?.['essay-student']?.['submission_order'] < 3);
  const isAnnulled = essay?.status === 'annulled';
  const correctionReady = isAnnulled || ['reviewed', 'unreviewed', 'reviewing'].includes(essay?.status);
  const correctionStudentId = essay?.['essay-student']?.id;

  return (
    <DashboardContent
      variant="plain"
      breadcrumbs={breadcrumbs}
    >
      {loading ? (
        <Loader />
      ) : (
        <>
          <TiaBanner
            className="mb-6 md:mt-12"
            image={tiaRedacaoConvite}
            bubbleMaxWidth={800}
            text='Antes de escrever, leia com calma os requisitos da redação, pois eles dizem exatamente o que o seu texto precisa ter. Assim você já acerta de primeira! Quando estiver pronto, clique em "Enviar a redação"'
          />

          <div className="mb-6 inline-flex items-center gap-1 rounded-full border border-neutral-lighter bg-white p-1">
            <Link
              to={PATHS.ESSAY.ABOUT(essay?.id)}
              className="-translate-y-px rounded-full px-4 py-1.5 text-sm font-extrabold text-white shadow-[0_3px_0_var(--color-mission-dark)]"
              style={{ background: 'var(--color-mission)' }}
            >
              Sobre o tema
            </Link>

            {correctionStudentId && correctionReady ? (
              <Link
                to={PATHS.ESSAY.CONSULT(essay?.id, correctionStudentId)}
                className="rounded-full px-4 py-1.5 text-sm font-semibold text-neutral-dark"
              >
                Correção
              </Link>
            ) : (
              <span className="cursor-not-allowed rounded-full px-4 py-1.5 text-sm font-semibold text-neutral-light">Correção</span>
            )}
          </div>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_360px]">
            <div className="flex flex-col gap-4">
              {essay?.status === 'processing' && (
                <Alert className="self-center bg-mission-light">
                  <FiInfo />
                  <AlertTitle>Sua redação está sendo corrigida. O resultado estará disponível aqui em breve.</AlertTitle>
                </Alert>
              )}

              {(canSend || isAnnulled) && (
                <Link
                  to={isAnnulled ? PATHS.ESSAY.CONSULT(essay?.id, correctionStudentId) : PATHS.ESSAY.SEND(essay?.id)}
                  className="block w-full rounded-full py-3.5 text-center text-sm font-extrabold text-white transition-transform duration-100 active:translate-y-px"
                  style={{ background: 'var(--gradient-btn-continue)', boxShadow: '0 3px 0 var(--color-mission-dark)' }}
                >
                  {isAnnulled ? 'Acessar redação' : 'Enviar a redação'}
                </Link>
              )}

              <div className="flex flex-wrap gap-4">
                <div className="flex h-20 flex-1 items-center justify-center gap-3 rounded-2xl border border-neutral-lightest bg-white px-4 shadow-card">
                  <img
                    src={calendarIcon}
                    alt=""
                    className="h-[50px] w-[50px] shrink-0"
                  />
                  <span className="text-sm text-neutral-dark">
                    Entregue a redação até <br />
                    <strong className="text-base text-neutral-900">{moment(essay?.['delivery-date']).format('DD/MM/YYYY')}</strong>
                  </span>
                </div>

                <div className="flex h-20 flex-1 items-center justify-center gap-3 rounded-2xl border border-neutral-lightest bg-white px-4 shadow-card">
                  <img
                    src={notesIcon}
                    alt=""
                    className="h-[50px] w-[50px] shrink-0"
                  />
                  <span className="text-sm text-neutral-dark">
                    Essa redação permite <br />
                    <strong className="text-base text-neutral-900">{essay?.['allow-multiple-submissions'] ? '2 envios' : '1 envio'} para correção</strong>
                  </span>
                </div>
              </div>

              <AboutCard title="Enunciado">
                <p className="mb-3 whitespace-pre-wrap text-sm text-neutral-dark">{essay?.statement}</p>
                <p className="mb-0 text-sm text-neutral-dark">
                  Tema: {essay?.topic}
                  <br />
                  Título: <strong className="text-neutral-900">{essay?.title}</strong>
                </p>
              </AboutCard>

              <AboutCard title="Tema">
                <p className="mb-0 text-sm text-neutral-dark">{essay?.topic}</p>
              </AboutCard>

              <AboutCard title="Textos motivadores">
                <div className="flex flex-col gap-4">
                  {essay?.['essay-motivator-texts']?.map((item, index) => (
                    <div key={item.id}>
                      <h4 className="mb-1 text-sm font-extrabold text-neutral-900">Texto {ROMAN_NUMERALS[index] ?? index + 1}</h4>
                      <div
                        className="text-sm text-neutral-dark"
                        dangerouslySetInnerHTML={{ __html: item.body }}
                      />
                    </div>
                  ))}
                </div>
              </AboutCard>
            </div>

            <div
              className="relative h-fit rounded-2xl border border-neutral-lightest p-5 shadow-card"
              style={{ background: 'linear-gradient(180deg, #F4FDFF 0%, #F2FCFF 44%, #FFFCF1 87.5%)' }}
            >
              <img
                src={tiaSurpresa}
                alt=""
                className="pointer-events-none absolute -right-2 -top-4 h-[90px] w-auto -scale-x-100 object-contain"
              />
              <div className="absolute right-0 top-[74px] h-1.5 w-16 rounded-l-full bg-[#A7DDE0]" />

              <h3 className="relative z-10 mb-6 max-w-[80%] text-base font-extrabold text-neutral-900">Como eu envio minha redação?</h3>

              <div className="relative z-10 flex flex-col gap-4">
                {HOW_TO_SEND_STEPS(essay?.id).map((step, index) => (
                  <div
                    key={index}
                    className="flex gap-3"
                  >
                    <span className="flex h-[30px] w-[30px] shrink-0 items-center justify-center text-2xl leading-none">
                      {step.emoji ?? (
                        <img
                          src={step.icon}
                          alt=""
                          className="h-[30px] w-[30px]"
                        />
                      )}
                    </span>
                    <div>
                      <h4 className="mb-1 text-sm font-extrabold text-neutral-900">{step.title}</h4>
                      <div className="text-xs text-neutral-dark">{step.description}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </DashboardContent>
  );
}
